/**
 * **L'équipe** — l'écran de la délégation ⟦SPEC-PRODUCTEUR §3, §5.5, §6.3, §11.6 · A-66 · M3.29⟧.
 *
 * C'est le **troisième maillon** de l'escalier : le moteur choisit (`engine/producteur.ts`,
 * `engine/manager.ts`), l'API transporte (`laisser_tourner`, `regler_equipe` avec
 * `manager_actif` et `consignes.rythme`), le contenu alimente (`content/producteur.json`) —
 * et sans cet écran, rien de tout ça ne serait atteignable par un geste de joueur.
 *
 * ── Ce que §11.6 demandait, et qui est là ────────────────────────────────────────
 *
 * 1. **DEUX blocs de cadrans** : la ligne éditoriale (cap, régime, tendance, et le 4e cadran
 *    `budget_video_max` — « s'il ne rentre pas dedans, il ne produit pas ») pour le
 *    PRODUCTEUR ; le rythme (cadence, repos, nœud visé) pour le MANAGER.
 * 2. **Le récapitulatif ANIME les compteurs** ⟦§5.5⟧ : l'écran rejoue les jours écoulés —
 *    quatre compteurs qui montent et quatre tracés qui se dessinent, jour par jour. « Tu as
 *    juste à faire avancer les jours et à regarder tes compteurs augmenter. »
 * 3. **Congédier / rappeler**, étage par étage — et la règle §6.3 : congédier le producteur
 *    suspend le manager, qui revient SANS prime avec lui.
 *
 * ⟦§3.4⟧ Toujours pas de cadran « sujet », « événements » ni « stratégie » — le premier
 * redonnerait un clic par vidéo, le deuxième déléguerait le seul contenu écrit du jeu, le
 * troisième serait une soluce déguisée ⟦A-42⟧.
 *
 * ## ⟦C57 · lot 5b · A-174⟧ Moins de texte : un nom, un tarif, des cadrans en mots-clés
 *
 * Mesuré avant le chantier : 167 mots visibles avec un producteur (527 en faisant défiler), dont la
 * présentation de 44 à 50 mots, des cadrans aux libellés de 9 mots et six paragraphes de règles. À
 * l'écran : le nom, **une phrase** de présentation (`description_courte`), le salaire en chiffres,
 * des cadrans à UN mot-clé avec leur pictogramme (`libelles_courts`), les boutons. **Tout le texte
 * d'avant est dans « Les dessous »** (le pictogramme 📰 de chaque section et de chaque candidat),
 * mot pour mot, et le libellé long d'un cadran reste au survol du bouton. Le récapitulatif des
 * jours garde ses compteurs, ses tracés et son résumé ; le détail par cause (l'argent, les jauges,
 * les vidéos, ce qui s'est passé) s'ouvre dans un seul « Les dessous ». Rien n'est supprimé : les
 * textes courts sont écrits à côté des longs dans `content/producteur.json` et `managers.json`.
 *
 * ## ⟦C62 · lot 4 · friction 4⟧ L'outil de la fin de partie ne doit plus être un mur
 *
 * Mesuré avant (partie naturelle d'un délégant, manager recruté, 1280 x 800) : 1 750 px de contenu pour une
 * fenêtre de 670, 38 boutons, un menu déroulant de 29 lignes, 258 mots, et « Laisser tourner » tout en bas, sans
 * aucun retour visible. Maintenant :
 *
 *  - **« Laisser tourner » est dans le pied de la fenêtre** (toujours visible, jamais sous le pli) et dans le
 *    panneau Actions de la chambre (`BoutonLaisserTourner.tsx`) ; les deux passent par `laisserTourner()`
 *    (`retourEquipe.ts`), donc rendent le même retour : un toast, et le récapitulatif **en haut** de l'écran ;
 *  - **les consignes sont repliées** derrière une ligne qui dit les valeurs en cours (les défauts du moteur
 *    tournent tant qu'on ne règle rien) ; le choix d'un producteur ou d'un manager l'est aussi ;
 *  - **le nœud visé n'est plus un menu de 29 lignes** : « Tout signaler » (le défaut) ou l'un des nœuds qui
 *    sont à portée (prérequis pris), six au plus ;
 *  - **un seul pictogramme « Les dessous » par fenêtre**, dans la barre de titre : tout le texte d'avant y est.
 *
 * Rien n'est supprimé : les cadrans, les prix, les règles de la réunion, les trois vitesses y sont toujours.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";

import {
  JOURS_MAX_DELEGUES,
  actionCost,
  consignes as consignesDe,
  equipeActive,
  managerActive,
  managerPossede as managerPossedeDe,
  memesConsignes,
  primeRetourManager,
  primeRetourProducteur,
  producteurActif as producteurActifDe,
  producteurPossede,
  salaireManager,
  salaireProducteur,
  collectModifiers,
  reposParDefaut,
  rythmeParDefaut,
  type Cadence,
  type CapEditorial,
  type Consignes,
  type ConsignesRythme,
  type GameState,
  type JourDelegue,
  type PolitiqueRepos,
  type RapportTendance,
  type RecapDelegue,
  type RegimeFormats,
} from "@maxime/shared";

import { DessousBloc, DessousLignes, LesDessous } from "../../components/LesDessous";
import { Panneau } from "../../components/Panneau";
import { contenu } from "../../game/content";
import { libelleCauseArgent, libelleClasse, ligneQuotidienne } from "../../game/delegation";
import { nombre } from "../../game/format";
import { entreesDeResolution } from "../../game/journal";
import { interrupteurs } from "../../game/menage";
import { JAUGE_RECAP, deltaLisible, libelleCause } from "../../game/recap";
import { useRunStore } from "../../stores/runStore";
import { dialog } from "../../stores/uiStore";

import { laisserTourner, useRetourEquipe } from "./retourEquipe";
import { resumerDelegation, retourVisible } from "./resumeDelegation";

const CAPS: readonly CapEditorial[] = ["lumiere", "neutre", "chaos"];
const REGIMES: readonly RegimeFormats[] = ["shorts", "mixte", "longues", "evergreen"];
const TENDANCES: readonly RapportTendance[] = ["surfer", "indifferent", "intemporel"];
const CADENCES_ORDRE: readonly Cadence[] = ["soutenue", "raisonnable", "econome"];
const REPOS_ORDRE: readonly PolitiqueRepos[] = ["jamais", "therapie", "tout"];

/**
 * ⟦§3.3 · C62 bis⟧ Les défauts du rythme : ce que la boucle joue tant qu'on ne règle rien. Le repos est celui du
 * CONTENU (`reposParDefaut`, `meta.repos_par_defaut`) : la même lecture que le moteur, sinon l'écran dirait « Jamais »
 * d'une équipe qui souffle déjà, et le premier réglage de cadence figerait « Jamais » dans les consignes sans que
 * le joueur l'ait choisi.
 */
const rythmeDefaut = (c: Parameters<typeof reposParDefaut>[0]): ConsignesRythme =>
  rythmeParDefaut(reposParDefaut(c));

/** ⟦§5.2 ✎ 6⟧ Trois crans, et le troisième est celui qui fait le sel de la mécanique. */
const VITESSES: readonly { jours: number | undefined; libelle: string; aide: string }[] = [
  // 🔑 **« Une action » et non « un jour »**, et ce n'est pas un détail de copie : une action
  // est atomique, et une longue coûte trois jours ⟦A-21⟧. Le cran rend UNE action — mesuré :
  // 1 action, 72 h. L'appeler « un jour » ferait mentir l'interface au premier clic.
  { jours: 1, libelle: "Une action", aide: "Le pas à pas, pour regarder faire. Une longue coûte trois jours." },
  { jours: 7, libelle: "Une semaine", aide: "Sept jours au moins — l'action en cours va au bout." },
  {
    jours: undefined,
    // ⟦C57 · A-174⟧ Le libellé d'avant (« Jusqu'à ce qu'on ait besoin de moi ») est au survol et dans les dessous.
    libelle: "Jusqu'à mon tour",
    aide: `Jusqu'à ce qu'on ait besoin de moi. Au plus ${JOURS_MAX_DELEGUES} jours. Ça s'arrête au premier moment qui t'appartient.`,
  },
];

/** ⟦C62 · lot 4⟧ Dans le pied de la fenêtre : « Jusqu'à mon tour » d'abord, c'est le geste de la fin de partie. */
const VITESSES_AFFICHEES: readonly (typeof VITESSES)[number][] = [VITESSES[2]!, VITESSES[1]!, VITESSES[0]!];

/** Combien de nœuds à portée on propose comme « nœud visé » : une poignée, jamais la liste de l'arbre. */
const NOEUDS_PROPOSES = 6;

export function Equipe() {
  const run = useRunStore((s) => s.run);
  const act = useRunStore((s) => s.act);
  const pending = useRunStore((s) => s.pending);
  const dernierRecap = useRetourEquipe((s) => s.recap);
  const dernierRunId = useRetourEquipe((s) => s.runId);
  const c = contenu();
  const [brouillon, setBrouillon] = useState<Consignes | null>(null);
  // ⟦C62 · lot 4⟧ Les consignes et le recrutement sont repliés : le joueur qui ne règle rien ne les voit pas.
  const [consignesOuvertes, setConsignesOuvertes] = useState(false);
  const [recrutementOuvert, setRecrutementOuvert] = useState(false);
  // ⟦C60 · A-177⟧ Les interrupteurs du ménage. L'Équipe reste ENTIÈRE (le producteur, le manager, la
  // délégation : Matt s'en sert pour automatiser la fin de partie) ; seul ce qui ne dépend que des
  // systèmes coupés disparaît d'ici : le cap lumière / neutre / chaos (l'alignement ne départage plus
  // rien), la « ligne éditoriale » qui le portait, la troisième ligne fixe « après les charges », l'arrêt
  // sur « un jalon jouable ».
  const sys = interrupteurs();

  if (!run) return null;
  const state = run.state;

  // ⟦§9⟧ L'interrupteur, lu du contenu — l'écran ne se dessine pas quand la mécanique dort.
  if (!equipeActive(c)) return null;

  const mods = collectModifiers(state, c);
  const possede = producteurPossede(mods);
  const actif = producteurActifDe(state, mods);
  // ⟦A-77 §4 · M3.29⟧ L'étage 3 a SON interrupteur : sans lui, pas un pixel du manager.
  const etage3 = managerActive(c);
  const managerPossede = etage3 && managerPossedeDe(mods);
  const managerEnPoste = managerPossede && state.flags.manager_actif !== false && actif;
  const managerSuspendu = managerPossede && state.flags.manager_suspendu_avec_producteur === true;

  const courantes = consignesDe(state);
  const vues = brouillon ?? courantes;
  const rythmeVu = vues.rythme ?? rythmeDefaut(c);
  const libelles = c.equipe.libelles;
  // ⟦C57 · A-174⟧ Le libellé COURT d'un cadran (un mot-clé et son pictogramme) ; le long reste au survol
  // du bouton et dans les dessous. Une clé sans court retombe sur le long : l'état neutre d'avant.
  const longs = libelles as Record<string, Record<string, string> | undefined>;
  const libelleLong = (famille: "cap" | "regime" | "tendance" | "cadence" | "repos", v: string) =>
    longs[famille]?.[v] ?? v;
  const libelleCourt = (famille: "cap" | "regime" | "tendance" | "cadence" | "repos", v: string) =>
    c.equipe.libelles_courts?.[famille]?.[v] ?? libelleLong(famille, v);
  const salaire = salaireProducteur(state, c);
  const prime = primeRetourProducteur(state, c);
  const salaireMan = salaireManager(state, c);
  const primeMan = primeRetourManager(state, c);
  const impayes = state.counters.producteur_jours_impayes ?? 0;
  const impayesMan = state.counters.manager_jours_impayes ?? 0;

  // ⟦C8 · A-120⟧ Le candidat RECRUTÉ, ou le défaut compat (Béa / Gérard) — absent de choix,
  // le nom et le prix affichés ici sont exactement ceux d'hier.
  const candidatProducteurId = state.flags.producteur_candidat_id;
  const candidatProducteurActuel = (c.equipe.candidats_producteur ?? []).find(
    (cand) => cand.id === candidatProducteurId,
  );
  const producteurNom = candidatProducteurActuel?.nom ?? c.equipe.producteur.nom;
  const producteurDescription = candidatProducteurActuel?.description ?? c.equipe.producteur.description;
  // ⟦C57 · A-174⟧ La forme courte est celle de la MÊME personne que la longue : jamais le court du
  // défaut posé sur un candidat qui n'en a pas.
  const producteurCourt = candidatProducteurActuel
    ? (candidatProducteurActuel.description_courte ?? candidatProducteurActuel.description)
    : (c.equipe.producteur.description_courte ?? c.equipe.producteur.description);

  const candidatManagerId = state.flags.manager_candidat_id;
  const candidatManagerActuel = c.managers.find((cand) => cand.id === candidatManagerId);
  const managerNom = candidatManagerActuel?.nom ?? c.equipe.manager.nom;
  const managerDescription = candidatManagerActuel?.description ?? c.equipe.manager.description;
  const managerCourt = candidatManagerActuel
    ? (candidatManagerActuel.description_courte ?? candidatManagerActuel.description)
    : (c.equipe.manager.description_courte ?? c.equipe.manager.description);

  // ⟦A-11 §1⟧ Le coût affiché EST le coût débité : même fonction, même charge utile.
  const coutReunion = actionCost(
    { type: "regler_equipe", producteur_actif: actif, consignes: vues },
    state,
  );
  const modifiee = !memesConsignes(vues, courantes) || JSON.stringify(vues.rythme ?? null) !== JSON.stringify(courantes.rythme ?? null);
  // ⟦C57 · A-174⟧ Le coût d'un CHANGEMENT de ligne, lu du moteur : sans changement en cours, `coutReunion`
  // vaut 0 (la réunion ne se facture que si la ligne bouge) et la ligne « Changer la ligne : 0 h » mentirait.
  // On demande donc au moteur le coût d'un changement de cap, jamais un chiffre recopié.
  const capAilleurs: CapEditorial = vues.editoriale.cap === "chaos" ? "neutre" : "chaos";
  // ⟦C60 · A-177⟧ Le cap n'est plus un cadran (alignement coupé) : le coût d'un changement se demande au moteur
  // avec un autre cadran qui bouge, le régime de formats. La réunion coûte pareil, quel que soit le cadran.
  const regimeAilleurs: RegimeFormats = vues.editoriale.regime === "mixte" ? "shorts" : "mixte";
  const coutChangement = modifiee
    ? coutReunion
    : actionCost(
        {
          type: "regler_equipe",
          producteur_actif: actif,
          consignes: {
            ...vues,
            editoriale: sys.alignement
              ? { ...vues.editoriale, cap: capAilleurs }
              : { ...vues.editoriale, regime: regimeAilleurs },
          },
        },
        state,
      );

  function regler(patch: Partial<Consignes["editoriale"]>) {
    setBrouillon({ ...vues, editoriale: { ...vues.editoriale, ...patch } });
  }
  function reglerRythme(patch: Partial<NonNullable<Consignes["rythme"]>>) {
    setBrouillon({ ...vues, rythme: { ...rythmeVu, ...patch } });
  }

  async function envoyer(producteur_actif: boolean, manager_actif?: boolean) {
    await act({
      type: "regler_equipe",
      producteur_actif,
      ...(manager_actif === undefined ? {} : { manager_actif }),
      consignes: vues,
    });
    setBrouillon(null);
  }

  /**
   * ⟦C8 · A-120⟧ Recruter (ou changer) un candidat — dialogue maison, jamais `window.confirm`
   * (CLAUDE.md). C'est une décision de build : elle coûte la réunion, comme un changement de
   * ligne (même refus/coût que `regler_equipe` sans candidat, cf. `actions.ts`).
   */
  async function recruter(role: "producteur" | "manager", id: string, nom: string, prixLabel: string) {
    // ⟦A-11 §1⟧ Le coût affiché EST le coût débité : même fonction, la charge utile de CETTE recrue
    // (recruter coûte la réunion même si la ligne n'a pas bougé).
    const coutRecrue = actionCost(
      {
        type: "regler_equipe",
        producteur_actif: actif,
        ...(managerPossede ? { manager_actif: managerEnPoste } : {}),
        ...(role === "producteur" ? { producteur_candidat_id: id } : { manager_candidat_id: id }),
        consignes: vues,
      },
      state,
    );
    const ok = await dialog.confirm({
      title: `Recruter ${nom} ?`,
      // ⟦C57 · A-174⟧ Deux phrases courtes : le prix, la réunion. « Comme tout changement de ligne » est dans les dessous.
      body: `${prixLabel} Réunion : ${Math.round(coutRecrue.hours)} h, −${Math.abs(coutRecrue.energy_delta)} ⚡.`,
      confirmLabel: "Recruter",
      cancelLabel: "Pas maintenant",
    });
    if (!ok) return;
    await act({
      type: "regler_equipe",
      producteur_actif: actif,
      ...(managerPossede ? { manager_actif: managerEnPoste } : {}),
      ...(role === "producteur" ? { producteur_candidat_id: id } : { manager_candidat_id: id }),
      consignes: vues,
    });
  }

  // ⟦C62 · lot 4⟧ Le dernier tour de la boucle, pour CETTE partie, s'il est encore frais (sept jours de jeu).
  const recap = retourVisible({ recap: dernierRecap, runId: dernierRunId }, run.runId, state);

  /** L'arbre moins ce qui est déjà acheté. */
  const noeudsVisables = c.skills.filter((sk) => !state.skills.includes(sk.id));
  // ⟦C62 · lot 4⟧ Les nœuds À PORTÉE (prérequis pris), les moins chers d'abord : le menu de 29 lignes devient
  // une poignée de boutons. Le nœud déjà visé reste proposé même s'il n'est plus à portée (rien ne se perd).
  const aPortee = noeudsVisables
    .filter((sk) => sk.prerequis.every((p) => state.skills.includes(p)))
    .sort((a, b) => a.tier - b.tier || a.cout_pc - b.cout_pc)
    .slice(0, NOEUDS_PROPOSES);
  const cibleDeja = rythmeVu.noeud_vise === null ? undefined : noeudsVisables.find((sk) => sk.id === rythmeVu.noeud_vise);
  const noeudsProposes = cibleDeja && !aPortee.some((sk) => sk.id === cibleDeja.id) ? [...aPortee, cibleDeja] : aPortee;

  // ⟦C57 · A-174⟧ Les phrases d'avant, mot pour mot : elles quittent l'écran et s'ouvrent dans « Les dessous ».
  const coutProducteurLong = actif ? (
    <>
      Elle te coûte <strong>{salaire} ₵ par jour</strong>, qu'elle produise ou non.
      {impayes > 0 ? (
        <>
          {" "}
          <strong>
            ⚠ {impayes} jour{impayes > 1 ? "s" : ""} impayé{impayes > 1 ? "s" : ""}
          </strong>{" "}
          — au troisième, elle part et tu reprends les ciseaux.
        </>
      ) : null}
    </>
  ) : (
    <>
      Elle ne te coûte plus rien. La rappeler coûte <strong>{prime} ₵</strong> — une porte de sortie
      ne doit pas être un piège payant, mais revenir se paie.
    </>
  );
  const coutManagerLong = managerEnPoste ? (
    <>
      Il te coûte <strong>{salaireMan} ₵ par jour</strong> —{" "}
      {sys.charges
        ? "la troisième ligne fixe, après les charges et le producteur."
        : "le deuxième salaire, après le producteur."}
      {impayesMan > 0 ? (
        <>
          {" "}
          <strong>
            ⚠ {impayesMan} jour{impayesMan > 1 ? "s" : ""} impayé{impayesMan > 1 ? "s" : ""}
          </strong>{" "}
          — son compteur à lui : les deux paies sont indépendantes.
        </>
      ) : null}
    </>
  ) : managerSuspendu ? (
    <>
      Sans producteur, il n'a rien à cadencer : il attend, <strong>sans salaire</strong> — et il
      reviendra <strong>sans prime</strong> avec elle.
    </>
  ) : (
    <>
      Il ne te coûte plus rien. Le rappeler coûte <strong>{primeMan} ₵</strong>.
    </>
  );
  const reunionLong = `Une réunion d'équipe coûte ${Math.round(coutChangement.hours)} h et ${Math.abs(coutChangement.energy_delta)} ⚡ quand ${sys.alignement ? "la ligne change" : "les consignes changent"} — congédier et rappeler, eux, sont immédiats. Se raviser trois fois dans la semaine coûte une demi-journée de travail : c'est fait exprès.`;

  // ⟦C62 · lot 4⟧ La ligne qui dit les consignes EN COURS (celles du moteur, pas le brouillon) : le joueur sait
  // ce que l'équipe fait sans déplier quoi que ce soit.
  const budgetEnCours = courantes.editoriale.budget_video_max;
  const rythmeEnCours = courantes.rythme ?? rythmeDefaut(c);
  const resumeConsignes = [
    ...(sys.alignement ? [libelleCourt("cap", courantes.editoriale.cap)] : []),
    libelleCourt("regime", courantes.editoriale.regime),
    libelleCourt("tendance", courantes.editoriale.tendance),
    budgetEnCours === null ? "sans plafond" : `plafond ${nombre(budgetEnCours)} ₵`,
    ...(managerPossede ? [libelleCourt("cadence", rythmeEnCours.cadence)] : []),
  ].join(" · ");

  // ⟦C62 · lot 4⟧ UN seul pictogramme « Les dessous » pour toute la fenêtre, dans la barre de titre : tout le
  // texte qu'on a sorti de l'écran (présentations, prix, règles de la réunion, vitesses, détail du dernier tour)
  // est ici, mot pour mot.
  const dessous = (
    <LesDessous titre="L'équipe" variante="icone">
      {!possede ? (
        <>
          <DessousBloc>
            <p className="dessous-texte">
              Personne ne travaille pour toi. <strong>{producteurNom}</strong> attend dans
              l'arbre, branche Business — et elle ne bouge pas avant 10 000 abonnés.
            </p>
          </DessousBloc>
          <DessousBloc titre={producteurNom}>
            <p className="dessous-texte">{producteurDescription}</p>
          </DessousBloc>
        </>
      ) : (
        <>
          <DessousBloc titre={producteurNom}>
            <p className="dessous-texte">{producteurDescription}</p>
            <p className="dessous-texte">{coutProducteurLong}</p>
          </DessousBloc>
          {/* ⟦A-77⟧ Le rôle, dans les mots de Mathieu, désormais normatifs : « le producteur
              automatise le choix des vidéos. » L'écran le dit tel quel — et dit aussi ce qui
              ne se délègue jamais. */}
          <DessousBloc titre={sys.alignement ? "La ligne éditoriale" : "Les consignes"}>
            <p className="dessous-texte">
              Elle <strong>automatise le choix des vidéos</strong> — le sujet, le format, le
              moment — selon {sys.alignement ? "la ligne que tu règles" : "les consignes que tu règles"} ici.
              Les événements, eux, restent à toi.
              Toujours.
            </p>
          </DessousBloc>
          {sys.alignement ? (
            <DessousBloc titre="Cap éditorial">
              <DessousLignes lignes={CAPS.map((v) => ({ texte: libelleLong("cap", v) }))} />
            </DessousBloc>
          ) : null}
          <DessousBloc titre="Régime de formats">
            <DessousLignes lignes={REGIMES.map((v) => ({ texte: libelleLong("regime", v) }))} />
          </DessousBloc>
          <DessousBloc titre="Rapport à la tendance">
            <DessousLignes lignes={TENDANCES.map((v) => ({ texte: libelleLong("tendance", v) }))} />
          </DessousBloc>
          <DessousBloc titre="Budget par vidéo">
            <p className="dessous-texte">
              Pas de plafond — elle embauche au mieux. …ou fixe un plafond en ₵ : au-delà, elle
              ne produit pas.
            </p>
          </DessousBloc>
          <DessousBloc titre="Une réunion d'équipe">
            <p className="dessous-texte">{reunionLong}</p>
          </DessousBloc>
          {managerPossede ? (
            <>
              <DessousBloc titre={managerNom}>
                <p className="dessous-texte">{managerDescription}</p>
                <p className="dessous-texte">{coutManagerLong}</p>
              </DessousBloc>
              <DessousBloc titre="Le rythme">
                <p className="dessous-texte">
                  Ce que ses 16 points achètent, exactement : deux raisons de moins d'être interrompu
                  — la santé mentale (selon le repos) et la trésorerie (il replie sur le moins
                  cher). Les événements, jamais.
                </p>
                <p className="dessous-texte">
                  ⚠ <strong>Soutenue est le seul cadran qui porte un risque</strong> : il publie
                  jusque dans le crunch (qualité ×0,7, santé mentale qui plonge) et la zone
                  rouge, où les coups pleuvent double. Déléguer en soutenue, c'est déléguer aussi
                  ton burn-out.
                </p>
              </DessousBloc>
              <DessousBloc titre="Cadence">
                <DessousLignes lignes={CADENCES_ORDRE.map((v) => ({ texte: libelleLong("cadence", v) }))} />
              </DessousBloc>
              <DessousBloc titre="Repos">
                <DessousLignes lignes={REPOS_ORDRE.map((v) => ({ texte: libelleLong("repos", v) }))} />
              </DessousBloc>
              <DessousBloc titre="Nœud visé">
                <p className="dessous-texte">
                  La seule passerelle vers l'arbre, à sens unique : il <strong>signale</strong>{" "}
                  quand CE nœud devient achetable — il n'achète jamais. Sans nœud visé, il
                  signale tout ce qui devient achetable.
                </p>
              </DessousBloc>
              <DessousBloc titre="Congédier">
                <p className="dessous-texte">Congédier le manager : gratuit, immédiat.</p>
              </DessousBloc>
            </>
          ) : null}
          <DessousBloc titre="Laisser tourner">
            <p className="dessous-texte">
              Le jeu avance tout seul et s'arrête au premier moment qui t'appartient : un
              événement, un palier, un nœud à acheter{sys.jalons ? ", un jalon jouable" : ""}. Tu peux
              reprendre la main à tout instant — il n'y a rien à annuler et rien à payer.
              {managerEnPoste
                ? " Avec le manager, la santé mentale et la trésorerie ne t'arrêtent plus — selon tes cadrans."
                : ""}
            </p>
            <DessousLignes
              lignes={VITESSES.map((v) => ({
                texte: (
                  <>
                    <strong>{v.jours === undefined ? "Jusqu'à ce qu'on ait besoin de moi" : v.libelle}</strong> :{" "}
                    {v.aide}
                  </>
                ),
              }))}
            />
          </DessousBloc>
          {recap !== null ? (
            <>
              <DessousBloc titre="Le motif d'arrêt">
                <p className="dessous-texte">{libelles.arret[recap.arret] ?? "On a besoin de toi."}</p>
              </DessousBloc>
              <RecapComplet recap={recap} state={state} />
              {(() => {
                const dernierJour = recap.jours_detail[recap.jours_detail.length - 1];
                const ligneLongue = dernierJour ? ligneQuotidienne(c.equipe.journal, dernierJour) : null;
                return ligneLongue ? (
                  <DessousBloc titre="Le dernier jour">
                    <p className="dessous-texte">{ligneLongue}</p>
                  </DessousBloc>
                ) : null;
              })()}
            </>
          ) : null}
        </>
      )}
    </LesDessous>
  );

  return (
    <Panneau
      titre="🎬 L'équipe"
      large
      actions={dessous}
      piedDePage={
        possede && actif ? (
          // ⟦C62 · lot 4⟧ « Laisser tourner » dans le PIED : hors du défilement, donc jamais sous le pli.
          <div className="equipe-pied">
            <span className="equipe-pied-titre">Laisser tourner</span>
            <div className="equipe-pied-vitesses">
              {VITESSES_AFFICHEES.map((v) => (
                <button
                  key={v.libelle}
                  className={v.jours === undefined ? "bouton primaire" : "bouton discret"}
                  type="button"
                  disabled={pending}
                  title={v.aide}
                  onClick={() => void laisserTourner(v.jours)}
                >
                  {v.libelle}
                </button>
              ))}
            </div>
          </div>
        ) : undefined
      }
    >
      {!possede ? (
        <>
          <p className="ligne-info">Personne ne travaille pour toi.</p>
          <p className="ligne-info">
            <strong>{producteurNom}</strong> : arbre, branche Business, dès 10 000 abonnés.
          </p>
        </>
      ) : (
        <>
          <section className="equipe-postes">
            <Poste role="Producteur" nom={producteurNom} court={producteurCourt} statut={actif ? null : "au chômage technique"}>
              <p className="ligne-info">
                {actif ? (
                  <>
                    Salaire : <strong>{salaire} ₵</strong> par jour.
                  </>
                ) : (
                  <>
                    Au repos : rappel <strong>{prime} ₵</strong>.
                  </>
                )}
              </p>
              {actif && impayes > 0 ? (
                <p className="ligne-info">
                  <strong>
                    ⚠ {impayes} jour{impayes > 1 ? "s" : ""} impayé{impayes > 1 ? "s" : ""}
                  </strong>{" "}
                  : au troisième, elle part.
                </p>
              ) : null}
              <div className="actions">
                {actif ? (
                  <button
                    className="bouton discret"
                    type="button"
                    disabled={pending}
                    title="Congédier (gratuit, immédiat)"
                    onClick={() => void envoyer(false)}
                  >
                    Congédier (gratuit)
                  </button>
                ) : (
                  <button className="bouton" type="button" disabled={pending} onClick={() => void envoyer(true)}>
                    {`Rappeler ${c.equipe.producteur.nom} (${prime} ₵)`}
                  </button>
                )}
              </div>
            </Poste>

            {/* ⟦§3.3 / §11.6 · M3.29⟧ Le MANAGER n'apparaît qu'une fois l'étage 3 ouvert — un cadran qui ne
                commande rien serait exactement ce qu'⟦A-66 §8.3⟧ refusait de livrer. */}
            {managerPossede ? (
              <Poste
                role="Manager"
                nom={managerNom}
                court={managerCourt}
                statut={managerEnPoste ? null : managerSuspendu ? "en attente du producteur" : "congédié"}
              >
                <p className="ligne-info">
                  {managerEnPoste ? (
                    <>
                      Salaire : <strong>{salaireMan} ₵</strong> par jour.
                    </>
                  ) : managerSuspendu ? (
                    <>
                      Sans producteur : <strong>sans salaire</strong>, <strong>sans prime</strong> au retour.
                    </>
                  ) : (
                    <>
                      Au repos : rappel <strong>{primeMan} ₵</strong>.
                    </>
                  )}
                </p>
                {managerEnPoste && impayesMan > 0 ? (
                  <p className="ligne-info">
                    <strong>
                      ⚠ {impayesMan} jour{impayesMan > 1 ? "s" : ""} impayé{impayesMan > 1 ? "s" : ""}
                    </strong>{" "}
                    : sa paie est à part.
                  </p>
                ) : null}
                <div className="actions">
                  {managerEnPoste ? (
                    <button
                      className="bouton discret"
                      type="button"
                      disabled={pending}
                      title="Congédier le manager (gratuit, immédiat)"
                      onClick={() => void envoyer(true, false)}
                    >
                      Congédier le manager
                    </button>
                  ) : managerSuspendu ? null : (
                    <button
                      className="bouton"
                      type="button"
                      disabled={pending || !actif}
                      onClick={() => void envoyer(true, true)}
                    >
                      Rappeler {managerNom} ({primeMan} ₵)
                    </button>
                  )}
                </div>
              </Poste>
            ) : null}
          </section>

          {/* ⟦C62 · lot 4⟧ Le dernier tour EN HAUT : c'est la première chose qu'on veut lire en revenant. */}
          {recap !== null ? (
            <Recapitulatif recap={recap} gabarits={c.equipe.journal_court ?? c.equipe.journal} />
          ) : null}

          <Pliant
            titre={sys.alignement ? "Ligne éditoriale" : "Consignes"}
            resume={modifiee ? "à valider" : resumeConsignes}
            ouvert={consignesOuvertes}
            force={modifiee}
            onBasculer={() => setConsignesOuvertes((o) => !o)}
          >
            <p className="ligne-info">Elle choisit les vidéos, pas les événements.</p>

            {/* ⟦C60 · A-177⟧ Le cap lumière / neutre / chaos ne départage plus rien (l'alignement est coupé) : le
                cadran n'existe plus, la consigne reste au neutre. Rallumé, il revient tel quel. */}
            {sys.alignement ? (
              <Cadran
                titre="Cap éditorial"
                options={CAPS}
                valeur={vues.editoriale.cap}
                libelle={(v) => libelleCourt("cap", v)}
                libelleLong={(v) => libelleLong("cap", v)}
                onChoix={(cap) => regler({ cap })}
              />
            ) : null}
            <Cadran
              titre="Régime de formats"
              options={REGIMES}
              valeur={vues.editoriale.regime}
              libelle={(v) => libelleCourt("regime", v)}
              libelleLong={(v) => libelleLong("regime", v)}
              onChoix={(regime) => regler({ regime })}
            />
            <Cadran
              titre="Rapport à la tendance"
              options={TENDANCES}
              valeur={vues.editoriale.tendance}
              libelle={(v) => libelleCourt("tendance", v)}
              libelleLong={(v) => libelleLong("tendance", v)}
              onChoix={(tendance) => regler({ tendance })}
            />

            {/* ⟦§3.2 / §4.2 critère 0⟧ **Le 4e cadran : le plafond d'UNE vidéo déléguée.**
                « S'il ne rentre pas dedans, il ne produit pas » — le moteur le tient
                (actionProduction), l'écran le règle enfin. */}
            <div className="cadran">
              <h4>Budget par vidéo</h4>
              <button
                type="button"
                className={vues.editoriale.budget_video_max === null ? "bouton" : "bouton discret"}
                title="Pas de plafond — elle embauche au mieux"
                onClick={() => regler({ budget_video_max: null })}
              >
                Pas de plafond
              </button>
              <label className="champ champ-budget">
                <span
                  className="champ-label"
                  title={
                    vues.editoriale.budget_video_max === null
                      ? undefined
                      : `Plafond : ${nombre(vues.editoriale.budget_video_max)} ₵ par vidéo — au-delà, elle ne produit pas.`
                  }
                >
                  {vues.editoriale.budget_video_max === null
                    ? "…ou fixe un plafond en ₵ :"
                    : `Plafond : ${nombre(vues.editoriale.budget_video_max)} ₵ par vidéo.`}
                </span>
                <input
                  type="number"
                  min={0}
                  step={50}
                  value={vues.editoriale.budget_video_max ?? ""}
                  placeholder="ex. 400"
                  onChange={(e) => {
                    const v = e.target.value === "" ? null : Math.max(0, Math.round(Number(e.target.value)));
                    regler({ budget_video_max: Number.isNaN(v as number) ? null : v });
                  }}
                />
              </label>
            </div>

            {/* ⟦§3.3 / §11.6 · M3.29⟧ **Le DEUXIÈME bloc de cadrans : le rythme, lu par le MANAGER.** */}
            {managerPossede ? (
              <>
                <h4 className="bloc-titre">Le rythme du manager</h4>
                <Cadran
                  titre="Cadence"
                  options={CADENCES_ORDRE}
                  valeur={rythmeVu.cadence}
                  libelle={(v) => libelleCourt("cadence", v)}
                  libelleLong={(v) => libelleLong("cadence", v)}
                  onChoix={(cadence) => reglerRythme({ cadence })}
                />
                {rythmeVu.cadence === "soutenue" ? (
                  <p className="ligne-info">
                    ⚠ <strong>Soutenue</strong> : qualité ×0,7, santé mentale qui plonge.
                  </p>
                ) : null}
                <Cadran
                  titre="Repos"
                  options={REPOS_ORDRE}
                  valeur={rythmeVu.repos}
                  libelle={(v) => libelleCourt("repos", v)}
                  libelleLong={(v) => libelleLong("repos", v)}
                  onChoix={(repos) => reglerRythme({ repos })}
                />
                {/* ⟦C62 · lot 4⟧ Plus de menu déroulant de 29 lignes : « tout signaler » ou l'un des nœuds à portée. */}
                <div className="cadran">
                  <h4>Nœud visé</h4>
                  <button
                    type="button"
                    className={rythmeVu.noeud_vise === null ? "bouton" : "bouton discret"}
                    title="Il signale tout ce qui devient achetable."
                    onClick={() => reglerRythme({ noeud_vise: null })}
                  >
                    Tout signaler
                  </button>
                  {noeudsProposes.map((sk) => (
                    <button
                      key={sk.id}
                      type="button"
                      className={rythmeVu.noeud_vise === sk.id ? "bouton" : "bouton discret"}
                      title={`Il signale quand « ${sk.nom} » devient achetable (${sk.cout_pc} points). Il n'achète jamais.`}
                      onClick={() => reglerRythme({ noeud_vise: sk.id })}
                    >
                      {sk.nom}
                    </button>
                  ))}
                </div>
              </>
            ) : null}

            <p className="ligne-info equipe-cout-reunion">
              {sys.alignement ? "Changer la ligne" : "Changer les consignes"} :{" "}
              <strong>{Math.round(coutChangement.hours)} h</strong> ·{" "}
              <strong>−{Math.abs(coutChangement.energy_delta)} ⚡</strong>
            </p>
            {!actif && modifiee ? <p className="ligne-info">Elles s'appliqueront au rappel.</p> : null}
            <div className="actions">
              {actif ? (
                <button
                  className="bouton primaire"
                  type="button"
                  disabled={pending || !modifiee}
                  onClick={() => void envoyer(true)}
                >
                  {sys.alignement ? "Valider la ligne" : "Valider les consignes"}
                </button>
              ) : null}
              {brouillon !== null ? (
                <button className="bouton discret" type="button" onClick={() => setBrouillon(null)}>
                  Annuler mes changements
                </button>
              ) : null}
            </div>
          </Pliant>

          {(c.equipe.candidats_producteur ?? []).length > 0 || c.managers.length > 0 ? (
            <Pliant
              titre="Recruter"
              resume="un autre producteur ou manager"
              ouvert={recrutementOuvert}
              onBasculer={() => setRecrutementOuvert((o) => !o)}
            >
              {/* ⟦C8 · A-120⟧ Le marché des producteurs — recruter, c'est choisir QUI tient la boutique ; son
                  salaire remplace la constante unique d'hier. ⟦C62⟧ La phrase de présentation est sous le nom,
                  on la lit AVANT de recruter ; la longue est dans « Les dessous » de la fenêtre. */}
              {(c.equipe.candidats_producteur ?? []).length > 0 ? (
                <div className="bloc-recrutement">
                  <h4 className="bloc-titre">Producteur</h4>
                  <ul className="liste-candidats">
                    {c.equipe.candidats_producteur!.map((cand) => (
                      <li key={cand.id} className="candidat">
                        <span className="candidat-nom">
                          {cand.nom}
                          {cand.id === candidatProducteurId ? " (en poste)" : ""}
                        </span>
                        <span className="candidat-prix">
                          {cand.salaire_jour} ₵/j · prime {cand.prime_retour} ₵
                        </span>
                        <button
                          type="button"
                          className="bouton discret"
                          disabled={pending || cand.id === candidatProducteurId}
                          onClick={() =>
                            void recruter(
                              "producteur",
                              cand.id,
                              cand.nom,
                              `${cand.salaire_jour} ₵ par jour, prime de retour ${cand.prime_retour} ₵.`,
                            )
                          }
                        >
                          Recruter
                        </button>
                        <span className="candidat-court">{cand.description_courte ?? cand.description}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {/* ⟦C8 · A-120⟧ Le vivier des managers recrutables — alignement (le style) et expérience (la
                  qualité) se lisent sur `managerTranche`, jamais ici : l'écran ne fait que nommer et chiffrer. */}
              {managerPossede && c.managers.length > 0 ? (
                <div className="bloc-recrutement">
                  <h4 className="bloc-titre">Manager</h4>
                  <ul className="liste-candidats">
                    {c.managers.map((cand) => (
                      <li key={cand.id} className="candidat">
                        <span className="candidat-nom">
                          {cand.nom}
                          {cand.id === candidatManagerId ? " (en poste)" : ""}
                        </span>
                        <span className="candidat-prix">
                          {cand.salaire_jour} ₵/j · prime {cand.prime_retour} ₵
                        </span>
                        <button
                          type="button"
                          className="bouton discret"
                          disabled={pending || cand.id === candidatManagerId}
                          onClick={() =>
                            void recruter(
                              "manager",
                              cand.id,
                              cand.nom,
                              `${cand.salaire_jour} ₵ par jour, prime de retour ${cand.prime_retour} ₵.`,
                            )
                          }
                        >
                          Recruter
                        </button>
                        <span className="candidat-court">{cand.description_courte ?? cand.description}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </Pliant>
          ) : null}
        </>
      )}
    </Panneau>
  );
}

/** Un poste de l'équipe : son nom, ce qu'il fait en une phrase, sa paie, et ce qu'on peut en faire. */
function Poste({
  role,
  nom,
  court,
  statut,
  children,
}: {
  role: string;
  nom: string;
  court: string;
  statut?: string | null;
  children?: ReactNode;
}) {
  return (
    <div className="equipe-poste">
      <div className="equipe-poste-tete">
        <h3>{nom}</h3>
        <span className="equipe-role">{role}</span>
        {statut ? <span className="equipe-statut">{statut}</span> : null}
      </div>
      <p className="ligne-info">{court}</p>
      {children}
    </div>
  );
}

/**
 * ⟦C62 · lot 4⟧ Un bloc replié par défaut : une ligne (le titre, ce qui est en cours), et son contenu au clic.
 * `force` le garde ouvert (des changements à valider ne se cachent pas).
 */
function Pliant({
  titre,
  resume,
  ouvert,
  force = false,
  onBasculer,
  children,
}: {
  titre: string;
  resume: string;
  ouvert: boolean;
  force?: boolean;
  onBasculer: () => void;
  children: ReactNode;
}) {
  const deplie = ouvert || force;
  return (
    <section className={`equipe-pliant${deplie ? " ouvert" : ""}`}>
      <button
        type="button"
        className="equipe-pliant-tete"
        aria-expanded={deplie}
        disabled={force}
        onClick={onBasculer}
      >
        <span className="equipe-pliant-fleche" aria-hidden="true">
          {deplie ? "▾" : "▸"}
        </span>
        <span className="equipe-pliant-titre">{titre}</span>
        <span className="equipe-pliant-resume">{resume}</span>
      </button>
      {deplie ? <div className="equipe-pliant-corps">{children}</div> : null}
    </section>
  );
}

function Cadran<T extends string>({
  titre,
  options,
  valeur,
  libelle,
  libelleLong,
  onChoix,
}: {
  titre: string;
  options: readonly T[];
  valeur: T;
  /** ⟦C57 · A-174⟧ Le libellé COURT, visible sur le bouton. */
  libelle: (v: T) => string;
  /** Le libellé d'avant, au survol du bouton (et dans les dessous de la section). */
  libelleLong: (v: T) => string;
  onChoix: (v: T) => void;
}) {
  return (
    <div className="cadran">
      <h4>{titre}</h4>
      {options.map((o) => (
        <button
          key={o}
          type="button"
          className={o === valeur ? "bouton" : "bouton discret"}
          title={libelleLong(o)}
          onClick={() => onChoix(o)}
        >
          {libelle(o)}
        </button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ⟦§5.5 / §11.6⟧ Le récapitulatif ANIMÉ — les compteurs qui montent
// ─────────────────────────────────────────────────────────────────────────────

/** Ce que chacune des quatre courbes suit dans `jours_detail`. */
const COURBES: readonly { cle: keyof Pick<JourDelegue, "abonnes" | "argent" | "energie" | "sm">; nom: string; icone: string; suffixe: string }[] = [
  { cle: "abonnes", nom: "Abonnés", icone: "🧑‍💻", suffixe: "" },
  { cle: "argent", nom: "Clicks", icone: "₵", suffixe: " ₵" },
  { cle: "energie", nom: "Énergie", icone: "⚡", suffixe: "" },
  { cle: "sm", nom: "Santé mentale", icone: "❤", suffixe: "" },
];

/** Cadence de l'animation : un jour simulé toutes les `MS_PAR_JOUR` millisecondes. */
const MS_PAR_JOUR = 220;

/**
 * ⟦§5.5⟧ **« Le moteur est appelé une fois et rend un état ; “pendant” veut donc dire “à
 * l'arrivée”. Ce que le client peut faire — et doit faire — c'est rejouer les jours écoulés en
 * animant les compteurs. »** C'est exactement ce que fait ce composant : `jours_detail` (une
 * entrée par jour simulé, ≤ 30) est rejoué jour par jour — quatre compteurs qui montent,
 * quatre tracés qui se dessinent, les vidéos posées en jalons. La surface la plus vue du
 * dernier tiers, dit la spec — elle méritait mieux qu'un tableau.
 */
function Recapitulatif({
  recap,
  gabarits,
}: {
  recap: RecapDelegue;
  /** ⟦§11.4⟧ Les gabarits de la ligne quotidienne, s'il y en a dans le contenu. ⟦C57 · A-174⟧ Leur forme COURTE. */
  gabarits: readonly string[] | undefined;
}) {
  const jours = recap.jours_detail;
  const [visibles, setVisibles] = useState(1);
  const minuteur = useRef<number | null>(null);
  const section = useRef<HTMLElement>(null);
  const dejaVu = useRef(recap);

  // Rejouer à chaque nouveau récapitulatif — et laisser cliquer pour aller au bout.
  useEffect(() => {
    setVisibles(1);
    // ⟦C62 · lot 4⟧ Un NOUVEAU tour arrive pendant que la fenêtre est ouverte (la fenêtre avait défilé) : on le
    // ramène à l'écran. À l'ouverture, on ne touche à rien : le haut de la fenêtre (les postes) reste le haut.
    if (dejaVu.current !== recap) {
      dejaVu.current = recap;
      section.current?.scrollIntoView?.({ block: "nearest" });
    }
    if (jours.length <= 1) return;
    minuteur.current = window.setInterval(() => {
      setVisibles((v) => {
        if (v >= jours.length) {
          if (minuteur.current !== null) window.clearInterval(minuteur.current);
          return v;
        }
        return v + 1;
      });
    }, MS_PAR_JOUR);
    return () => {
      if (minuteur.current !== null) window.clearInterval(minuteur.current);
    };
  }, [recap, jours.length]);

  const courant = jours[Math.min(visibles, jours.length) - 1];
  const fini = visibles >= jours.length;
  const resume = resumerDelegation(recap, contenu().equipe.libelles_courts?.arret);

  return (
    <section className="equipe-recap" ref={section}>
      {/* ⟦C62 · lot 4⟧ Une ligne, la même que le toast : ce que l'équipe a fait et pourquoi elle rend la main.
          Le détail par cause (l'argent, les jauges, les vidéos, ce qui s'est passé), le motif en toutes lettres et
          le dernier jour sont dans « Les dessous » de la fenêtre. */}
      <h3>Pendant ton absence</h3>
      <p className="equipe-recap-phrase">{resume.phrase}</p>
      {recap.salaires > 0 ? (
        <p className="ligne-info">
          Salaires versés : {nombre(recap.salaires)} ₵. Caisse : {nombre(recap.argent.avant)} →{" "}
          {nombre(recap.argent.apres)} ₵.
        </p>
      ) : null}

      {jours.length > 1 && courant ? (
        <div
          className="recap-anime"
          role="button"
          tabIndex={0}
          title={fini ? "" : "Clique pour aller au bout"}
          onClick={() => setVisibles(jours.length)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") setVisibles(jours.length);
          }}
        >
          {/* ⟦§11.4 · A-81⟧ La ligne quotidienne vient des gabarits du contenu quand il en
              porte ; sans eux, la ligne d'avant — l'état neutre. */}
          <p className="ligne-info discret">
            {fini ? "Rejoué jour par jour." : (ligneQuotidienne(gabarits, courant) ?? `Jour ${courant.jour}…`)}{" "}
            {courant.videos > 0 ? `🎬 ×${courant.videos}` : ""}
          </p>
          <div className="recap-courbes">
            {COURBES.map((courbe) => (
              <CourbeAnimee
                key={courbe.cle}
                jours={jours}
                visibles={visibles}
                cle={courbe.cle}
                nom={courbe.nom}
                icone={courbe.icone}
                suffixe={courbe.suffixe}
              />
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ⟦§1.4 / §5.5 · A-81⟧ Le récapitulatif complet — la cause, jamais le solde
// ─────────────────────────────────────────────────────────────────────────────

/** « +25 » / « −120 ₵ » : signe explicite, virgule française, jamais « +0 ». */
function signe(delta: number, suffixe = ""): string {
  return `${delta > 0 ? "+" : "−"}${nombre(Math.abs(delta))}${suffixe}`;
}

/** Combien de faits on raconte en toutes lettres avant de compter le reste. */
const FAITS_VISIBLES = 12;

/**
 * Les cinq lignes de §1.4, rendues **telles que le moteur les a mesurées** : l'argent cause par
 * cause, les abonnés gagnés ET perdus, chaque jauge avec sa décomposition, les vidéos produites,
 * et les faits marquants dans les mots du journal (la même traduction, `game/journal.ts` — un
 * récit écrit deux fois est un récit qui diverge). Un récapitulatif d'avant A-81 (sans ces
 * champs) ne rend rien de plus que l'en-tête : aucun chiffre n'est inventé.
 */
function RecapComplet({ recap, state }: { recap: RecapDelegue; state: GameState }) {
  const c = contenu();
  const argent = recap.argent_lignes ?? [];
  // ⟦C60 bis · A-177⟧ La réputation a quitté l'écran (arbitrage n° 2, 03/10/2026) : sa ligne ne se lit plus ici non plus.
  const reputationVisible = interrupteurs().reputation;
  const jauges = (recap.jauges ?? []).filter(
    (j) => j.jauge !== "argent" && j.jauge !== "abonnes" && (reputationVisible || j.jauge !== "reputation"),
  );
  const videos = recap.videos_detail ?? [];
  const faits = recap.faits ?? [];
  const { gagnes, perdus } = recap.abonnes;
  // Les faits, dans les mots du journal — et seulement ceux que le journal sait dire.
  const lignesFaits = entreesDeResolution(faits, [], state, 0);
  const visibles = lignesFaits.slice(0, FAITS_VISIBLES);

  if (argent.length === 0 && jauges.length === 0 && videos.length === 0 && faits.length === 0 && gagnes === undefined) {
    return null;
  }

  return (
    <div className="recap-complet">
      <div className="recap-bloc">
        <h4>Les abonnés</h4>
        <p className="ligne-info">
          {nombre(recap.abonnes.avant)} → <strong>{nombre(recap.abonnes.apres)}</strong>
          {gagnes !== undefined && perdus !== undefined ? (
            <>
              {" "}
              · <span className="ton-bien">+{nombre(gagnes)} gagnés</span>,{" "}
              <span className={perdus > 0 ? "ton-mal" : ""}>−{nombre(perdus)} perdus</span>
            </>
          ) : null}
        </p>
      </div>

      {argent.length > 0 ? (
        <div className="recap-bloc">
          <h4>L'argent</h4>
          <p className="ligne-info">
            {nombre(recap.argent.avant)} ₵ → <strong>{nombre(recap.argent.apres)} ₵</strong>
          </p>
          <ul className="recap-causes">
            {argent.map((l) => (
              <li key={l.cause} className={l.delta < 0 ? "ton-mal" : "ton-bien"}>
                <span>{libelleCauseArgent(l.cause)}</span>
                <strong>{signe(l.delta, " ₵")}</strong>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {jauges.length > 0 ? (
        <div className="recap-bloc">
          <h4>Les jauges</h4>
          <ul className="recap-causes recap-jauges">
            {jauges.map((j) => (
              <li key={j.jauge}>
                <span>
                  {JAUGE_RECAP[j.jauge].icone} {JAUGE_RECAP[j.jauge].nom} : {nombre(j.avant)} →{" "}
                  <strong>{nombre(j.apres)}</strong>
                </span>
                <ul className="recap-causes recap-sous-causes">
                  {j.lignes.map((l) => (
                    <li key={l.cause} className={l.delta < 0 ? "ton-mal" : "ton-bien"}>
                      <span>{libelleCause(l.cause, "laisser_tourner")}</span>
                      <strong>{deltaLisible(j.jauge, l.delta)}</strong>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {videos.length > 0 ? (
        <div className="recap-bloc">
          <h4>Les vidéos produites</h4>
          <ul className="recap-causes">
            {videos.map((v) => (
              <li key={v.id} className={v.classe === "nulle" ? "ton-mal" : ""}>
                <span>
                  « {c.parSujet.get(v.subject_id)?.nom ?? v.subject_id} » · {v.format === "short" ? "short" : "longue"} ·{" "}
                  {libelleClasse(v.classe)}
                </span>
                <strong>{nombre(v.execution_score)}/100</strong>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {visibles.length > 0 ? (
        <div className="recap-bloc">
          <h4>Ce qui s'est passé</h4>
          <ul className="journal recap-faits">
            {visibles.map((e) => (
              <li className={`journal-ligne ton-${e.ton}`} key={e.id}>
                <span aria-hidden="true">{e.icone}</span>
                <span>{e.texte}</span>
              </li>
            ))}
          </ul>
          {lignesFaits.length > visibles.length ? (
            <p className="ligne-info discret">
              … et {nombre(lignesFaits.length - visibles.length)} autre(s) — tout est dans le Journal.
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Un compteur qui monte et son tracé qui se dessine — une courbe des quatre. */
function CourbeAnimee({
  jours,
  visibles,
  cle,
  nom,
  icone,
  suffixe,
}: {
  jours: readonly JourDelegue[];
  visibles: number;
  cle: "abonnes" | "argent" | "energie" | "sm";
  nom: string;
  icone: string;
  suffixe: string;
}) {
  const n = Math.max(1, Math.min(visibles, jours.length));
  const valeurs = jours.map((j) => j[cle]);
  const min = Math.min(...valeurs);
  const max = Math.max(...valeurs);
  const largeur = 160;
  const hauteur = 36;
  const x = (i: number) => (jours.length === 1 ? 0 : (i / (jours.length - 1)) * largeur);
  const y = (v: number) => (max === min ? hauteur / 2 : hauteur - ((v - min) / (max - min)) * hauteur);
  const points = valeurs
    .slice(0, n)
    .map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`)
    .join(" ");
  const courant = valeurs[n - 1] ?? 0;

  return (
    <div className="recap-courbe">
      <span className="recap-courbe-nom">
        {icone} {nom}
      </span>
      <strong className="recap-courbe-valeur">
        {nombre(courant)}
        {suffixe}
      </strong>
      <svg
        viewBox={`-2 -2 ${largeur + 4} ${hauteur + 4}`}
        width="100%"
        height={hauteur + 4}
        aria-hidden="true"
        preserveAspectRatio="none"
      >
        <polyline points={points} fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
    </div>
  );
}
